export type SupportedChatLanguage = 'en' | 'fr'

export type ChatUiStrings = {
  header: string
  placeholder: string
  activePatientLabel: string
  noPatientSelected: string
  sending: string
  listening: string
  connectingVoice: string
  closePanel: string
  sendMessage: string
  send: string
  messageInput: string
  liveTranscription: string
  composerAriaLabel: string
  micRequestingPermission: string
  micConnecting: string
  micStopRecording: string
  micProcessingSpeech: string
  micVoiceError: string
  micStartRecording: string
  intentProcessing: string
  finishingTranscription: string
  /** Footer: mic stop → command shown in chat (voice). */
  footerVoiceToChat: string
  /** Footer: command shown → assistant response (voice intent). */
  footerIntent: string
  /** Footer: typed submit → normalized command shown. */
  footerNormalize: string
  /** Footer: typed command shown → assistant response. */
  footerTypedCommand: string
  /** Gap label between consecutive chat bubbles. */
  footerGapSincePrevious: string
}

/** i18next language key in host app ``localStorage``. */
export const I18NEXT_LANG_STORAGE_KEY = 'i18nextLng'

const CHAT_UI_STRINGS: Record<SupportedChatLanguage, ChatUiStrings> = {
  en: {
    header: 'Voice Assistant',
    placeholder: 'Type your message...',
    activePatientLabel: 'Active patient',
    noPatientSelected: 'No patient selected',
    sending: 'Sending...',
    listening: 'Listening…',
    connectingVoice: 'Connecting…',
    closePanel: 'Close',
    sendMessage: 'Send message',
    send: 'Send',
    messageInput: 'Message input',
    liveTranscription: 'Live transcription',
    composerAriaLabel: 'message composer',
    micRequestingPermission: 'Requesting microphone access',
    micConnecting: 'Connecting to voice service',
    micStopRecording: 'Stop recording',
    micProcessingSpeech: 'Processing speech',
    micVoiceError: 'Voice error',
    micStartRecording: 'Start recording',
    intentProcessing: 'Analyzing command…',
    finishingTranscription: 'Finishing…',
    footerVoiceToChat: 'voice→chat',
    footerIntent: 'intent',
    footerNormalize: 'normalize',
    footerTypedCommand: 'command',
    footerGapSincePrevious: 'since previous',
  },
  fr: {
    header: 'Assistant Vocal',
    placeholder: 'Tapez votre message...',
    activePatientLabel: 'Patient actif',
    noPatientSelected: 'Aucun patient sélectionné',
    sending: 'Envoi…',
    listening: 'Écoute…',
    connectingVoice: 'Connexion…',
    closePanel: 'Fermer',
    sendMessage: 'Envoyer le message',
    send: 'Envoyer',
    messageInput: 'Saisie du message',
    liveTranscription: 'Transcription en direct',
    composerAriaLabel: 'zone de saisie du message',
    micRequestingPermission: 'Demande d’accès au micro',
    micConnecting: 'Connexion au service vocal',
    micStopRecording: 'Arrêter l’enregistrement',
    micProcessingSpeech: 'Traitement de la parole',
    micVoiceError: 'Erreur vocale',
    micStartRecording: 'Démarrer l’enregistrement',
    intentProcessing: 'Analyse de la commande…',
    finishingTranscription: 'Finalisation…',
    footerVoiceToChat: 'voix→chat',
    footerIntent: 'intention',
    footerNormalize: 'normalisation',
    footerTypedCommand: 'commande',
    footerGapSincePrevious: 'depuis le précédent',
  },
}

function parseStoredLanguageTag(tag: string): SupportedChatLanguage | null {
  const t = tag.trim().toLowerCase()
  if (!t) return null
  if (t === 'fr' || t.startsWith('fr-')) return 'fr'
  if (t === 'en' || t.startsWith('en-')) return 'en'
  return null
}

/**
 * Chat UI language: ``localStorage.i18nextLng`` when set (``fr`` / ``en`` tags),
 * otherwise French.
 */
export function getSupportedLanguage(): SupportedChatLanguage {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(I18NEXT_LANG_STORAGE_KEY)
      if (stored != null && stored.trim()) {
        const parsed = parseStoredLanguageTag(stored)
        if (parsed) return parsed
      }
    } catch {
      /* localStorage blocked */
    }
  }
  return 'fr'
}

/** BCP 47 tag for ``Intl`` / ``toLocaleString`` in the chat UI. */
export function getIntlLocale(): string {
  return getSupportedLanguage() === 'fr' ? 'fr-FR' : 'en-US'
}

/** BCP 47 tag for browser Web Speech API (wake phrase), not AWS Transcribe. */
export function getSpeechRecognitionLocale(): string {
  return getSupportedLanguage() === 'fr' ? 'fr-FR' : 'en-US'
}

export function applyDocumentLang(lang: SupportedChatLanguage): void {
  if (typeof document === 'undefined') return
  document.documentElement.lang = lang
}

export function getChatUiStrings(lang: SupportedChatLanguage = getSupportedLanguage()): ChatUiStrings {
  return CHAT_UI_STRINGS[lang]
}

const KNOWN_ENGLISH_PANEL_TITLES = new Set([
  'voice assistant',
  'voice nav',
  'voice navigation',
])

const KNOWN_ENGLISH_PLACEHOLDERS = new Set([
  'type your message...',
  'type your message…',
  'type a message...',
  'type a message…',
])

function normalizeEmbedDefaultKey(value: string): string {
  return value.trim().toLowerCase().replace(/…/g, '...')
}

function isKnownEnglishEmbedDefault(value: string, known: Set<string>): boolean {
  return known.has(normalizeEmbedDefaultKey(value))
}

/** Host ``config.title`` — localized header when unset or a known English embed default. */
export function resolveChatPanelTitle(rawTitle: string | undefined, header: string): string {
  const title = typeof rawTitle === 'string' ? rawTitle.trim() : ''
  if (!title) return header
  if (/^\(?[A-Za-z]+Error\)?$/.test(title)) return header
  if (isKnownEnglishEmbedDefault(title, KNOWN_ENGLISH_PANEL_TITLES)) return header
  return title
}

/** Host ``config.placeholder`` — localized placeholder when unset or a known English embed default. */
export function resolveChatPlaceholder(
  rawPlaceholder: string | undefined,
  placeholder: string,
): string {
  const p = typeof rawPlaceholder === 'string' ? rawPlaceholder.trim() : ''
  if (!p) return placeholder
  if (isKnownEnglishEmbedDefault(p, KNOWN_ENGLISH_PLACEHOLDERS)) return placeholder
  return p
}

export function isFrenchLanguage(lang: SupportedChatLanguage = getSupportedLanguage()): boolean {
  return lang === 'fr'
}
